import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Table, Space, Form, Input, DatePicker, notification, Tooltip, Modal, Button } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useSelector } from "react-redux";
import { RootState } from "shared/redux/store";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { INRFormatter, numberWithCommas, saveByteArray } from "../../shared/services/Utility";
import { ReactComponent as EditIcon } from '../../assets/images/editIconBlack.svg';
import { ReactComponent as IconError } from '../../assets/images/error.svg';
import { ReactComponent as AddFileGray } from '../../assets/images/upload-file-gray.svg';
import API_SERVICE from '../../shared/services/api-service';
import "./index.scss";
import { RECONCILE_NUMBER_REGEX, RECONCILE_PERCENTAGE_REGEX } from 'shared/constants/AppConstants';
import AppUpload from "shared/components/Lead/AppUpload/app-upload";

interface DataType {
  key: string;
  disbursalId: string;
  caseId: string;
  lenderIdentifier: string;
  lenderName: string;
  borrowerName: string;
  loanProductName: string;
  disbursalDate: string;
  lenderDisbursementDate: string;
  disbursedAmount: number;
  lenderDisbursementAmount: number;
  insuranceAmount: number;
  revenuePercentage: number;
  revenueAmount: number;
  invoiceNo: string;
  invoiceDate: string;
  issueType: string;
  issueReason: string;
  errorDescription?: string;
  disbursementDifference?: number;
  connectorPayoutComputed?: number;
  connectorPayoutRevised?: number;
  payoutDifference?: number;
  adjustDiscrepanciesComment?: string;
  salesUserHierarchy?: string;
  reconcileStatus: string;
  invoiceDocumentId?: string;
  lenderInvoiceDocumentId?: string | null;
}

// Utility to format numeric fields for form display
function formatReconciliationFormValues(record: any) {
  return {
    ...record,
    lenderDisbursementAmount: record.lenderDisbursementAmount !== undefined ? numberWithCommas(record.lenderDisbursementAmount) : '',
    insuranceAmount: (record.insuranceAmount !== null || record.insuranceAmount !== undefined) ? numberWithCommas(record.insuranceAmount) : '',
    revenueAmount: (record.revenueAmount !== null || record.revenueAmount !== undefined) ? numberWithCommas(record.revenueAmount) : '',
    revenuePct: (record.revenuePercentage !== null || record.revenuePercentage !== undefined) ? record.revenuePercentage?.toString() : '',
    disbursedAmount: (record.disbursedAmount !== null || record.disbursedAmount !== undefined) ? numberWithCommas(record.disbursedAmount) : '',
    // Additional fields needed for this component
    invoiceNo: (record.invoiceNo !== null || record.invoiceNo !== undefined) ? record.invoiceNo : '',
    lenderDisbursementDate: record.lenderDisbursementDate ? moment(record.lenderDisbursementDate) : null,
    invoiceDate: record.invoiceDate ? moment(record.invoiceDate) : null
  };
}

interface Props {
  filterValues: any;
  onReconcileDataSequenceComplete?: () => void;
}

const ReconciliationReconciled = forwardRef<any, Props>(({ filterValues, onReconcileDataSequenceComplete }, ref) => {
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [mainForm] = Form.useForm();
  const { appColors }: any = useSelector((state: RootState) => state.common);
  const { searchParam } = useSelector((state: RootState) => state.reconciliation);

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [data, setData] = useState<DataType[]>([]);
  // state for totals
  const [totalData, setTotalData] = useState<{
    totalDisbursedAmount: string;
    totalLenderDisbursementAmount: string;
    totalRevenue: string;
  } | null>(null);

  const [showUpload, setShowUpload] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [importLoading, setImportLoading] = useState(false);

  // Define a loadData function for reuse in API callbacks
  const loadData = React.useCallback(() => {
    setLoading(true);
    API_SERVICE.getReconcileDisbursedCases({
      pageNo,
      pageSize,
      status: 'RECONCILED',
      searchParam,
      ...filterValues // Include filter values in the API call
    })
      .then((res: any) => {
        const content = (res?.data?.payload?.content || []).map((row: any) => ({
          ...row,
          key: row.disbursalId || row.key
        }));
        setData(content);
        setTotalCount(res?.data?.payload?.totalElements || 0);
        
        // Also refresh totals
        return API_SERVICE.getReconcileTotalCalculation({ 
          pageNo, 
          pageSize, 
          status: 'RECONCILED',
          searchParam,
          ...filterValues 
        });
      })
      .then((res: any) => {
        if (res && res.data) {
          setTotalData(res.data.payload);
        }
      })
      .catch((error) => {
        notification.error({ message: "Failed to load reconciled cases" });
      })
      .finally(() => {
        setLoading(false);
        onReconcileDataSequenceComplete?.();
      });
  }, [pageNo, pageSize, filterValues, searchParam, onReconcileDataSequenceComplete]);

  // Call loadData when component mounts or dependencies change
  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePaginationChange = (page: number, size?: number) => {
    setPageNo(page);
    if (size && size !== pageSize) setPageSize(size);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => setSelectedRowKeys(newSelectedRowKeys),
    preserveSelectedRowKeys: true,
  };

  const startEdit = (key: string) => {
    setEditingKey(key);

    const record = data.find(item => item.key === key);
    if (record) {
      // Use our enhanced format function that handles all fields
      const formattedValues = formatReconciliationFormValues(record);
      mainForm.setFieldsValue(formattedValues);
    }
  };

  const cancelEdit = () => {
    setEditingKey(null);
    mainForm.resetFields();
  };

  const validateNumber = (_: any, value: string) => {
    if (value && !RECONCILE_NUMBER_REGEX.test(value)) {
      return Promise.reject('Please enter numbers only');
    }
    return Promise.resolve();
  };
  const validatePercentage = (_: any, value: string | number) => {
    if (value === undefined || value === null || value === '') return Promise.resolve();
    let numValue: number;
    if (typeof value === 'number') {
      numValue = value;
    } else if (typeof value === 'string') {
      numValue = Number(value.replace(/,/g, ''));
    } else {
      return Promise.reject('Please enter a value between 0 and 100');
    }
    if (!RECONCILE_PERCENTAGE_REGEX.test(numValue.toString())) {
      return Promise.reject('Please enter a value between 0 and 100');
    }
    return Promise.resolve();
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const rawValue = e.target.value.replace(/[^\d]/g, '');
    if (rawValue) {
      mainForm.setFieldsValue({ [fieldName]: numberWithCommas(rawValue) });
    } else {
      mainForm.setFieldsValue({ [fieldName]: '' });
    }
  };

  const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    if (value) {
      const numValue = Number(value);
      const limitedValue = Math.min(100, numValue);
      mainForm.setFieldsValue({ revenuePct: limitedValue.toString() });
    } else {
      mainForm.setFieldsValue({ revenuePct: '' });
    }
  };

  const saveEdit = async () => {
    if (!editingKey) return;
    try {
      const values = await mainForm.validateFields();
      
      // Get the invoice number and date values
      const invoiceNo = mainForm.getFieldValue('invoiceNo');
      const invoiceDate = mainForm.getFieldValue('invoiceDate');
      const revenueAmount = mainForm.getFieldValue('revenueAmount');
      const revenuePct = mainForm.getFieldValue('revenuePct');
      
      // Check if all editable fields are empty - if so, don't do anything
      if (!invoiceNo && !invoiceDate && !revenueAmount && !revenuePct) {
        return;
      }
      
      // Validation: If Invoice No. is entered but Invoice Date is missing, show error
      if (invoiceNo && !invoiceDate) {
        // Apply errors to the appropriate fields
        const fieldErrors: { name: string; errors: string[] }[] = [];
        
        // If invoice no is provided but date is missing
        fieldErrors.push({
          name: 'invoiceDate',
          errors: ['Required']
        });
        
        mainForm.setFields(fieldErrors);
        return;
      }
      
      // Validation: If Invoice Date is entered but Invoice No. is missing, show error
      if (!invoiceNo && invoiceDate) {
        // Apply errors to the appropriate fields
        const fieldErrors: { name: string; errors: string[] }[] = [];
        
        // If invoice date is provided but invoice no is missing
        fieldErrors.push({
          name: 'invoiceNo',
          errors: ['Required']
        });
        
        mainForm.setFields(fieldErrors);
        return;
      }
      
      // Clean values (remove commas from formatted numbers)
      const cleanValues = {
        ...values,
        revenueAmount: values.revenueAmount ? Number(values.revenueAmount.replace(/,/g, '')) : undefined,
        revenuePercentage: values.revenuePct ? Number(values.revenuePct) : undefined,
      };
      
      // Format dates for the API
      const formattedInvoiceDate = values.invoiceDate ? moment(values.invoiceDate).format('YYYY-MM-DD') : undefined;
      
      // Prepare payload
      const record = data.find(item => item.key === editingKey);
      if (!record) return;
      
      // Create payload according to the required structure
      const payload = {
        disbursalId: record.disbursalId,
        revenuePercentage: cleanValues.revenuePercentage,
        revenueAmount: cleanValues.revenueAmount,
        // Only include invoiceNo if it has a value
        ...(values.invoiceNo ? { invoiceNo: values.invoiceNo } : {}),
        // Only include invoiceDate if it has a value
        ...(formattedInvoiceDate ? { invoiceDate: formattedInvoiceDate } : {}),
        // Mandatory fields for RECONCILED status
        lenderDisbursementDate: record.lenderDisbursementDate,
        lenderDisbursementAmount: record.lenderDisbursementAmount,
        insuranceAmount: record.insuranceAmount,
        reconcileStatus: "RECONCILED"  // Exact status value as required by API
      };
      
      // Send data to API
      setLoading(true);
      API_SERVICE.updateReconciliationDisbursal(payload)
        .then((res: any) => {
          // Clear editing state
          setEditingKey(null);
          
          // Refresh data after successful update
          loadData();
          
          notification.success({ message: "Reconciliation updated successfully." });
        })
        .catch((error: any) => {
          notification.error({ message: "Failed to update reconciliation." });
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (err) {
    }
  };

  const renderEditableRow = (record: DataType) => (
    <div className="reconciliation-edit-row">
      <Form
        form={mainForm}
        layout="inline"
        className="edit-form-inline material-ui-style"
        style={{ width: '100%' }}
      >
        <Form.Item
          label="Revenue %"
          name="revenuePct"
          rules={[{ validator: validatePercentage }]}
        >
          <Input
            size="small"
            className="reconciliation-field"
            placeholder="%"
            value={mainForm.getFieldValue('revenuePct')}
            onChange={handlePercentageChange}
          />
        </Form.Item>
        <Form.Item
          label="Revenue Amount"
          name="revenueAmount"
          rules={[{ validator: validateNumber }]}
        >
          <Input
            placeholder="Rs."
            size="small"
            className="reconciliation-field"
            value={mainForm.getFieldValue('revenueAmount')}
            onChange={e => handleAmountChange(e, 'revenueAmount')}
          />
        </Form.Item>
        <Form.Item 
          label="Invoice no." 
          name="invoiceNo"
          validateTrigger={["onChange", "submit"]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const dateValue = getFieldValue('invoiceDate');
                
                // Only check for validation when both values are relevant for comparison
                if (!value && !dateValue) {
                  return Promise.resolve(); // Both are empty, no validation needed
                }
                
                // If invoice date has value but this field is empty/missing
                if (!value && dateValue) {
                  return Promise.reject('Required');
                }
                
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input 
            size="small" 
            className="reconciliation-field"
            disabled={!!record.lenderInvoiceDocumentId || !!record.invoiceDocumentId}
            onChange={(e) => {
              // Only clear errors if the field has a value
              if (e.target.value) {
                // Clear the error from invoice date field if it exists
                const dateField = mainForm.getFieldError('invoiceDate');
                if (dateField && dateField.length > 0) {
                  mainForm.setFields([{
                    name: 'invoiceDate',
                    errors: []
                  }]);
                }
              }
            }}
          />
        </Form.Item>
        <Form.Item 
          label="Invoice date" 
          name="invoiceDate"
          validateTrigger={["onChange", "submit"]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const invoiceNo = getFieldValue('invoiceNo');
                
                // Only check for validation when both values are relevant for comparison
                if (!value && !invoiceNo) {
                  return Promise.resolve(); // Both are empty, no validation needed
                }
                
                // If invoice no has value but this field is missing
                if (!value && invoiceNo) {
                  return Promise.reject('Required');
                }
                
                return Promise.resolve();
              },
            }),
          ]}
        >
          <DatePicker 
            format="DD/MM/YYYY" 
            placeholder="Select date" 
            size="small" 
            className="reconciliation-field"
            disabled={!!record.lenderInvoiceDocumentId || !!record.invoiceDocumentId}
            onChange={(date) => {
              // Only clear errors if a date is selected
              if (date) {
                // Clear the error from invoice no field if it exists
                const noField = mainForm.getFieldError('invoiceNo');
                if (noField && noField.length > 0) {
                  mainForm.setFields([{
                    name: 'invoiceNo',
                    errors: []
                  }]);
                }
              }
            }}
          />
        </Form.Item>
        <div className="action-buttons-container">
          <CheckCircleOutlined onClick={saveEdit} className="action-check-icon" />
          <CloseCircleOutlined onClick={cancelEdit} className="action-close-icon" />
        </div>
      </Form>
    </div>
  );

  const columns: ColumnsType<DataType> = [
    {
      title: 'Disbursal ID',
      dataIndex: 'disbursalId',
      key: 'disbursalId',
      fixed: 'left',
      width: 140,
    },
    {
      title: 'Case ID',
      dataIndex: 'caseId',
      key: 'caseId',
      fixed: 'left',
      width: 120,
    },
    {
      title: 'Lender Identifier',
      dataIndex: 'lenderIdentifier',
      key: 'lenderIdentifier',
      width: 140,
      render: (value: string) => value ?? '',
    },
    {
      title: 'Lender',
      dataIndex: 'lenderName',
      key: 'lender',
      width: 140,
      render: (value: string) => value ?? '',
    },
    {
      title: 'Customer Name',
      dataIndex: 'borrowerName',
      key: 'customer',
      width: 160,
      render: (value: string) => value ?? '',
    },
    {
      title: 'Product',
      dataIndex: 'loanProductName',
      key: 'product',
      width: 200,
      render: (value: string) => value ?? '',
    },
    {
      title: 'Disbursement Date',
      dataIndex: 'disbursalDate',
      key: 'disbursalDate',
      width: 140,
    },
    {
      title: 'Lender Disbursement Date',
      dataIndex: 'lenderDisbursementDate',
      key: 'lenderdisbursalDate',
      width: 170,
    },
    {
      title: 'Disbursement Amount',
      dataIndex: 'disbursedAmount',
      key: 'disbursalAmount',
      width: 150,
      render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '',
    },
    {
      title: 'Lender Disbursement Amount',
      dataIndex: 'lenderDisbursementAmount',
      key: 'lenderdisbursalAmount',
      width: 200,
      render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '',
    },
    {
      title: 'Insurance Amount',
      dataIndex: 'insuranceAmount',
      key: 'insuranceAmount',
      width: 140,
      render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '',
    },
    {
      title: 'Revenue %',
      dataIndex: 'revenuePercentage',
      key: 'revenuePct',
      width: 110,
      render: (value: number) => value !== null && value !== undefined ? <span>{value}%</span> : '',
    },
    {
      title: 'Revenue Amount',
      dataIndex: 'revenueAmount',
      key: 'revenueAmount',
      width: 140,
      render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '',
    },
    {
      title: 'Invoice No.',
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      width: 120,
      render: (value: string) => value ?? '',
    },
    {
      title: 'Invoice Date',
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
      width: 130,
    },
    {
      title: 'Sales user hierarchy',
      dataIndex: 'salesUserHierarchy',
      key: 'salesUserHierarchy',
      width: 150,
      ellipsis: true,
      render: (value: any) =>(
        <Tooltip title={value}>
          {value}
        </Tooltip>
      )
    },
    {
      title: 'Rectify',
      key: 'rectify',
      align: 'center',
      width: 100,
      render: (_: any, record: any) =>
        record?.errorDescription?.length > 0 && (
          <Tooltip placement="topRight" title={record?.errorDescription}>
            <IconError width={20} height={20} />
          </Tooltip>
        ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <button
            className="action-link"
            onClick={() => startEdit(record.key)}
            title="Edit"
          >
            <EditIcon width={18} height={18} />
          </button>
        </Space>
      ),
    },
  ];

  // Export handler
  const handleExport = async () => {
    // Check if rows are selected
    if (!selectedRowKeys || selectedRowKeys.length === 0) {
      notification.info({ message: "Select the cases to export" });
      return;
    }

    setLoading(true);
    try {
      const payload: any = {
        disbursalIds: selectedRowKeys.map(key => String(key)),
        status: 'RECONCILED'
      };

      const response = await API_SERVICE.exportReconcileDisbursals(payload);

      const contentDisposition = response.headers["content-disposition"];
      let fileName = "reconciliation-reconciled.xlsx";
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch.length > 1) {
          fileName = fileNameMatch[1];
        }
      }
      saveByteArray([response.data], fileName);
      notification.success({ message: "File downloaded successfully." });
    } catch (e: any) {
      try {
        const errorText = await new Response(e?.response?.data).text();
        const parsedError = JSON.parse(errorText);
        notification.error({ message: API_SERVICE.handleErrors(parsedError) });
      } catch (parseError) {
        notification.error({ message: API_SERVICE.handleErrors(e) });
      }
    } finally {
      setLoading(false);
    }
  };

  // Import handler
  const handleImport = () => {
    setShowUpload(true);
    setUploadedFile(null);
  };

  const uploadedDocumentFile = async () => {
    if (!uploadedFile) {
      notification.error({ message: "Please upload .xlsx file!" });
      return;
    }
    setImportLoading(true);
    try {
      const entityType = "UPDATE_RECONCILE_INFO";
      const formData = new FormData();
      formData.append("file", uploadedFile);
      const res = await API_SERVICE.bulkUploadStart(entityType, formData);
      notification.success({ message: res?.data?.message || "Upload started" });
      await API_SERVICE.bulkUploadExecute(res?.data?.payload?.uploadId, entityType);
      notification.success({ message: "Import completed successfully" });
      setShowUpload(false);
      setUploadedFile(null);
      loadData(); // Refresh table after successful import
    } catch (e: any) {
      notification.error({ message: API_SERVICE.handleErrors(e) });
    } finally {
      setImportLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    handleExport,
    handleImport,
  }));

  return (
    <div className="reconciliation-table-wrapper reconciliation-material-ui">
      <Table
        rowKey="disbursalId"
        rowSelection={rowSelection}
        className="dynamic-pagination-checkbox table-full-width reconciliation-tight-table reconciliation-table-with-footer"
        style={{ '--bgColor': appColors?.appPrimaryColor ?? '' } as React.CSSProperties}
        scroll={{ x: 1800 }}
        size="small"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          current: pageNo,
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          position: ['bottomRight'],
          onChange: handlePaginationChange,
          onShowSizeChange: handlePaginationChange,
        }}
        expandable={{
          expandedRowRender: (record) => {
            if (editingKey === record.key) {
              return renderEditableRow(record);
            }
            return null;
          },
          expandedRowKeys: editingKey ? [editingKey] : [],
          rowExpandable: (record) => record.key === editingKey,
          expandIconColumnIndex: -1,
        }}
        footer={() => (
          <div className="reconciliation-table-custom-footer">
            <div className="reconciliation-table-footer-cell">
              Total Disbursed Amount: {INRFormatter(Number(totalData?.totalDisbursedAmount || '0'))}
            </div>
            <div className="reconciliation-table-footer-cell">
              Total Lender Disbursed Amount: {INRFormatter(Number(totalData?.totalLenderDisbursementAmount || '0'))}
            </div>
            <div className="reconciliation-table-footer-cell">
              Total Revenue: {INRFormatter(Number(totalData?.totalRevenue || '0'))}
            </div>
          </div>
        )}
      />
      <Modal
        width={300}
        visible={showUpload}
        style={{ marginBottom: 230 }}
        okText="Yes"
        cancelText="No"
        title="Upload Excel"
        centered
        closable={false}
        maskClosable={false}
        footer={false}
      >
        <AppUpload
          acceptType={['xlsx']}
          callBack={(file) => setUploadedFile(file?.file || file)}
          setFileSize={() => {}}
          setUploadedFile={setUploadedFile}
          setIsSelectedLenderDoc={() => {}}
          showUploadList={uploadedFile != null}
          disabled={importLoading}
          multiple={false}
          maxSize={10}
          className="upload-wrapper"
        >
          <AddFileGray />Upload Excel File
        </AppUpload>
        <Space direction="vertical" style={{ marginBottom: "15px" }}>
          <Button
            type="primary"
            className="export-button dynamic-btn-primary"
            style={{ width: "250px" }}
            loading={importLoading}
            onClick={uploadedDocumentFile}
          >
            Upload
          </Button>
          <Button
            onClick={() => {
              setShowUpload(false);
              setUploadedFile(null);
            }}
            className="export-button dynamic-btn-default"
            style={{ width: "250px" }}
          >
            Close
          </Button>
        </Space>
      </Modal>
    </div>
  );
});

export default ReconciliationReconciled;
